/**
 * Collaboration Store
 *
 * Manages trip sharing and collaborator information.
 */

import { create } from "zustand";
import type {
  TripShare,
  UserProfile,
  PermissionLevel,
  InviteLink,
} from "@poi-planner/shared";
import { api } from "../services/api";

function buildInviteUrl(token: string): string {
  return `${window.location.origin}/invite/${token}`;
}

interface CollaborationState {
  // State
  shares: TripShare[];
  inviteLinks: InviteLink[];
  userProfiles: Map<string, UserProfile>;
  loading: boolean;
  error: string | null;

  // Share Actions
  loadTripShares: (tripId: string) => Promise<void>;
  shareTrip: (
    tripId: string,
    email: string,
    permission: PermissionLevel,
  ) => Promise<void>;
  updatePermission: (
    shareId: string,
    permission: PermissionLevel,
  ) => Promise<void>;
  removeShare: (shareId: string) => Promise<void>;
  getUserProfile: (userId: string) => Promise<UserProfile | null>;
  clearShares: () => void;

  // Invite Link Actions
  loadInviteLinks: (tripId: string) => Promise<void>;
  createInviteLink: (
    tripId: string,
    permission: PermissionLevel,
    label?: string,
  ) => Promise<string>;
  cancelInviteLink: (linkId: string) => Promise<void>;
}

export const useCollaborationStore = create<CollaborationState>((set, get) => ({
  // Initial state
  shares: [],
  inviteLinks: [],
  userProfiles: new Map(),
  loading: false,
  error: null,

  // Load all shares for a trip
  loadTripShares: async (tripId: string) => {
    set({ loading: true, error: null });
    try {
      const shares = await api.getTripShares(tripId);
      set({ shares, loading: false });

      // Preload user profiles for all shared users
      for (const share of shares) {
        get().getUserProfile(share.sharedWithUserId);
      }
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },

  // Share trip with a user
  shareTrip: async (
    tripId: string,
    email: string,
    permission: PermissionLevel,
  ) => {
    set({ loading: true, error: null });
    try {
      const newShare = await api.shareTrip(tripId, email, permission);
      set((state) => ({
        shares: [...state.shares, newShare],
        loading: false,
      }));

      // Preload user profile
      get().getUserProfile(newShare.sharedWithUserId);
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },

  // Update permission level for a share
  updatePermission: async (shareId: string, permission: PermissionLevel) => {
    set({ loading: true, error: null });
    try {
      await api.updateSharePermission(shareId, permission);
      set((state) => ({
        shares: state.shares.map((s) =>
          s.id === shareId ? { ...s, permissionLevel: permission } : s,
        ),
        loading: false,
      }));
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },

  // Remove a share (revoke access)
  removeShare: async (shareId: string) => {
    set({ loading: true, error: null });
    try {
      await api.removeShare(shareId);
      set((state) => ({
        shares: state.shares.filter((s) => s.id !== shareId),
        loading: false,
      }));
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },

  // Get user profile (with caching)
  getUserProfile: async (userId: string) => {
    const { userProfiles } = get();

    // Return cached profile if available
    if (userProfiles.has(userId)) {
      return userProfiles.get(userId)!;
    }

    try {
      const profile = await api.getUserProfile(userId);
      if (profile) {
        set((state) => ({
          userProfiles: new Map(state.userProfiles).set(userId, profile),
        }));
      }
      return profile;
    } catch (err) {
      console.error("Failed to load user profile:", err);
      return null;
    }
  },

  // Clear all shares (when leaving trip view)
  clearShares: () => {
    set({ shares: [], inviteLinks: [], error: null });
  },

  // Load all invite links for a trip
  loadInviteLinks: async (tripId: string) => {
    set({ loading: true, error: null });
    try {
      const inviteLinks = await api.getInviteLinks(tripId);
      set({ inviteLinks, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },

  // Create a new invite link and return the full URL
  createInviteLink: async (
    tripId: string,
    permission: PermissionLevel,
    label?: string,
  ) => {
    set({ loading: true, error: null });
    try {
      const newLink = await api.createInviteLink(tripId, permission, label);
      set((state) => ({
        inviteLinks: [newLink, ...state.inviteLinks],
        loading: false,
      }));
      // Return the invite URL
      return buildInviteUrl(newLink.token);
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },

  // Cancel an invite link
  cancelInviteLink: async (linkId: string) => {
    set({ loading: true, error: null });
    try {
      await api.cancelInviteLink(linkId);
      set((state) => ({
        inviteLinks: state.inviteLinks.map((link) =>
          link.id === linkId ? { ...link, status: "cancelled" as const } : link,
        ),
        loading: false,
      }));
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      throw err;
    }
  },
}));
